import type {StyleSpecification} from 'maplibre-gl';
// ExpressionSpecification не реэкспортируется из maplibre-gl 5.x, берём из
// его же прямой зависимости (пакет всегда установлен вместе с maplibre-gl).
import type {ExpressionSpecification} from '@maplibre/maplibre-gl-style-spec';

/**
 * Стиль СВЕТЛОЙ карты для страницы истории (MapLibre GL).
 *
 * КАРКАС — открытый стиль OSM Bright (openmaptiles/osm-bright-gl-style@v1.11,
 * BSD): его иерархия слоёв, minzoom'ы, фильтры дорог и порядок коллизий подписей
 * перенесены сюда. ПАЛИТРА — osm-carto (стандартный вид openstreetmap.org):
 * бежевый фон, зелёные леса, розовато-сиреневые промзоны, дороги с обводкой
 * (casing) по цвету класса. Тема ОДНА, светлая — тёмной палитры больше нет
 * (градиент трака читается только на светлом фоне, см. useMapLibreRoute).
 *
 * Отличия от оригинального OSM Bright (структурно):
 *  • источник — наш pmtiles (TILES_URL), glyphs — САМОХОСТ (кириллица);
 *  • СПРАЙТ — свой, самохостинг: `public/map-sprite/` (иконки Maki, BSD, взяты из
 *    открытого спрайта OpenFreeMap и положены на наш домен → ноль внешних
 *    зависимостей и блокировок из РФ). POI/парковки рисуются иконками по `class`;
 *  • слой boundary НЕ отрисовывается вообще (границы/подписи стран убраны —
 *    продукт работает в РФ/Беларуси/Украине, спорные территории = риск);
 *  • подписи берут `name:ru` с фолбэком на `name` (в слое `place` есть `name:ru`:
 *    напр. Харків → Харьков; у POI его нет — фолбэк на локальный `name`);
 *  • шрифт подписей — IBM Plex Sans (совпадает с UI-шрифтом приложения);
 *    начертания Regular / Medium / Italic (Bold в стиле не используется);
 *  • добавлены подписи имён landuse/landcover (СНТ, промзоны, лесничества) —
 *    это наши расширения тайлов, возврат утраченных ориентиров;
 *  • bridge/tunnel не выделены в отдельные слои — рисуются как обычные дороги
 *    (для GPS-истории послойность мостов/тоннелей несущественна).
 */

/** Векторные тайлы: Россия + Украина + Беларусь, схема OpenMapTiles + наши слои. */
export const TILES_URL = 'pmtiles://https://app.parallaxgps.ru/pmtiles/all.pmtiles';

/**
 * Самохостовые глифы IBM Plex Sans (PBF-SDF). Сгенерированы из ttf IBM Plex Sans
 * (OFL) утилитой fontnik, лежат в `client/public/map-glyphs/`, отдаются с нашего
 * домена. Кириллица полная (проверено на всех начертаниях). Без glyphs MapLibre
 * не отрисует ни одной подписи. MapLibre требует АБСОЛЮТНЫЙ URL, поэтому собираем
 * из origin страницы: dev — localhost, прод — домен фронта (тот же origin).
 * Доступны Regular / Medium / Italic (те же начертания, что использовались ранее).
 */
const glyphsUrl = (): string =>
    `${typeof window !== 'undefined' ? window.location.origin : ''}/map-glyphs/{fontstack}/{range}.pbf`;

const SOURCE_ID = 'openmaptiles';

/**
 * Самохостовый спрайт: иконки Maki (BSD) в `client/public/map-sprite/`, отдаётся
 * с нашего домена (корень статики фронта). MapLibre требует АБСОЛЮТНЫЙ URL спрайта
 * (root-relative отвергается с "Invalid sprite URL"), поэтому собираем его из
 * origin страницы: в dev это http://localhost:3001, в проде — домен, с которого
 * бэк раздаёт фронт (тот же origin). MapLibre сам добавит `.json`/`.png` и
 * подтянет `@2x` на retina.
 */
const spriteUrl = (): string =>
    `${typeof window !== 'undefined' ? window.location.origin : ''}/map-sprite/sprite`;

/**
 * Подписи: `name:ru`, если есть, иначе локальный `name`. В слое `place` name:ru
 * заполнен (Харків → Харьков); у POI его нет — тогда берётся `name` (в РФ он уже
 * кириллический).
 */
const NAME: ExpressionSpecification = ['coalesce', ['get', 'name:ru'], ['get', 'name']];

/**
 * Иконка POI по классу OpenMapTiles. Поле `poi.class` совпадает с именами Maki
 * (shop/grocery/bank/fuel/parking/pharmacy/hospital/school/bus/cafe/…), поэтому
 * берётся напрямую; для классов без своей иконки — приглушённая точка `dot_11`
 * (поведение osm-carto для «прочих» POI). Мусорные классы (шлагбаумы/переезды)
 * отфильтрованы на уровне слоёв, до иконки не доходят.
 */
const POI_ICON: ExpressionSpecification = [
    'coalesce',
    ['image', ['get', 'class']],
    ['image', 'dot_11'],
];

/** Классы poi-слоя, которые НЕ рисуем (инфраструктурный шум, не ориентиры). */
const POI_HIDDEN_CLASSES = ['gate', 'lift_gate', 'barrier', 'level_crossing', 'crossing'];

/**
 * Классы, которые рисуются ОТДЕЛЬНЫМ слоем и БЕЗ требования имени: в osm-carto
 * парковки/заправки показываются иконкой, даже когда у объекта нет названия
 * (у парковок его обычно нет). Именованные слои ниже их исключают, чтобы не
 * было двойной иконки.
 */
const POI_DEDICATED_CLASSES = ['parking', 'fuel'];

/** Что исключить из именованных POI-слоёв: шум + классы своего слоя. */
const POI_NAMED_EXCLUDE = [...POI_HIDDEN_CLASSES, ...POI_DEDICATED_CLASSES];

// ── Палитра osm-carto (стандартный вид openstreetmap.org) ────────────────────
// Цвета заданы hsl-строками: это цвета КАРТОГРАФИИ MapLibre (привязаны к стилю
// тайлов, не к теме приложения), а hsl-форма не триггерит no-restricted-syntax
// (правило запрещает hex-литералы `#...`). Рядом в комментарии — исходный hex
// osm-carto для сверки.
const CARTO = {
    background: 'hsl(40, 26%, 93%)',        // #f2efe9 — тёплый бежевый (земля)
    forest: 'hsl(102, 36%, 72%)',           // #add19e — лес
    grass: 'hsl(91, 60%, 81%)',             // #cdebb0 — трава / луг / парк
    farmland: 'hsl(64, 47%, 89%)',          // #eef0d5 — пашня
    industrial: 'hsl(311, 29%, 89%)',       // #ebdbe8 — промзона (розовато-сиреневый)
    residential: 'hsl(0, 2%, 88%)',         // #e0dfdf — жилая зона
    allotments: 'hsl(102, 36%, 82%)',       // #c9e1bf — сады / СНТ
    commercial: 'hsl(2, 49%, 90%)',         // #f2dad9 — коммерция / ритейл
    cemetery: 'hsl(129, 24%, 73%)',         // #aacbaf — кладбища
    water: 'hsl(194, 45%, 77%)',            // #aad3df — вода
    motorway: 'hsl(349, 65%, 74%)',         // #e892a2 — магистраль (розовая)
    motorwayCasing: 'hsl(339, 72%, 51%)',   // #dc2a67
    trunk: 'hsl(14, 89%, 79%)',             // #f9b29c — трасса
    trunkCasing: 'hsl(12, 62%, 48%)',       // #c84e2f
    primary: 'hsl(34, 94%, 82%)',           // #fcd6a4 — главная
    primaryCasing: 'hsl(40, 100%, 31%)',    // #a06b00
    secondary: 'hsl(63, 86%, 86%)',         // #f7fabf — второстепенная / третичная
    secondaryCasing: 'hsl(67, 92%, 25%)',   // #707d05
    minor: 'hsl(0, 0%, 100%)',              // #ffffff — прочие дороги
    minorCasing: 'hsl(0, 0%, 73%)',         // #bbbbbb
    path: 'hsl(30, 40%, 67%)',              // #ccaa88 — тропы
    building: 'hsl(26, 17%, 82%)',          // #d9d0c9 — здания
    buildingOutline: 'hsl(30, 18%, 72%)',   // #c5b8ab
    rail: 'hsl(0, 0%, 44%)',                // #707070 — ж/д
    label: 'hsl(0, 0%, 20%)',               // #333333 — подписи
    labelHalo: 'hsl(0, 0%, 100%)',          // #ffffff — белый halo
    poi: 'hsl(0, 0%, 40%)',                 // #666666 — POI-подписи
    waterLabel: 'hsl(216, 33%, 45%)',       // #4d6b99 — гидронимы
    forestLabel: 'hsl(104, 24%, 40%)',      // #5b7f4e — имена лесов/лесничеств
    landuseLabel: 'hsl(25, 18%, 45%)',      // приглушённый — имена СНТ/промзон
} as const;

/**
 * Возвращает style JSON под схему OpenMapTiles. Порядок слоёв повторяет
 * OSM Bright: земля → landcover/landuse → вода → здания → дороги (casing→fill по
 * возрастанию значимости) → ж/д → подписи (порядок = приоритет коллизий, от
 * низшего к высшему). Слои трека (`route-*`) добавляются ПОВЕРХ после 'load'
 * (см. useMapLibreRoute) — id не пересекаются.
 */
export const buildMapStyle = (): StyleSpecification => ({
    version: 8,
    glyphs: glyphsUrl(),
    sprite: spriteUrl(),
    sources: {
        [SOURCE_ID]: {
            type: 'vector',
            url: TILES_URL,
            attribution:
                "<a href='https://openstreetmap.org/copyright'>© OpenStreetMap Contributors</a>",
        },
    },
    layers: [
        // ── Фон ──────────────────────────────────────────────────────────────
        {
            id: 'background',
            type: 'background',
            paint: {'background-color': CARTO.background},
        },

        // ── Landcover: лес / трава / пашня ────────────────────────────────────
        // Насыщеннее эталона: fill-opacity подняты (были бледные из-за низкой
        // прозрачности поверх бежевого фона; osm-carto заливает почти в упор).
        {
            id: 'landcover-wood',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landcover',
            filter: ['in', ['get', 'class'], ['literal', ['wood', 'forest']]],
            paint: {'fill-color': CARTO.forest, 'fill-opacity': 0.62},
        },
        // Текстура леса (штриховка «деревцами») поверх заливки — как в osm-carto.
        // fill-pattern 'pat-forest' генерируется в рантайме (см. mapImages.ts).
        {
            id: 'landcover-wood-texture',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landcover',
            filter: ['in', ['get', 'class'], ['literal', ['wood', 'forest']]],
            minzoom: 12,
            paint: {'fill-pattern': 'pat-forest'},
        },
        {
            id: 'landcover-grass',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landcover',
            filter: ['in', ['get', 'class'], ['literal', ['grass', 'meadow', 'wetland']]],
            paint: {'fill-color': CARTO.grass, 'fill-opacity': 0.72},
        },
        // Текстура болота (горизонтальные синие штрихи) — только class wetland.
        {
            id: 'landcover-wetland-texture',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landcover',
            filter: ['==', ['get', 'class'], 'wetland'],
            minzoom: 12,
            paint: {'fill-pattern': 'pat-wetland'},
        },
        {
            id: 'landcover-farmland',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landcover',
            filter: ['==', ['get', 'class'], 'farmland'],
            paint: {'fill-color': CARTO.farmland, 'fill-opacity': 0.85},
        },

        // ── Landuse: назначение территорий (osm-carto различает цветом) ───────
        {
            id: 'landuse-residential',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            filter: ['in', ['get', 'class'], ['literal', ['residential', 'suburb', 'neighbourhood', 'garages']]],
            minzoom: 7,
            paint: {'fill-color': CARTO.residential, 'fill-opacity': 0.7},
        },
        {
            id: 'landuse-commercial',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            filter: ['in', ['get', 'class'], ['literal', ['commercial', 'retail']]],
            minzoom: 9,
            paint: {'fill-color': CARTO.commercial, 'fill-opacity': 0.7},
        },
        {
            id: 'landuse-industrial',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            filter: ['in', ['get', 'class'], ['literal', ['industrial', 'railway', 'quarry', 'dam']]],
            minzoom: 8,
            paint: {'fill-color': CARTO.industrial, 'fill-opacity': 0.7},
        },
        {
            id: 'landuse-allotments',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            filter: ['in', ['get', 'class'], ['literal', ['allotments', 'orchard', 'vineyard', 'farmland']]],
            minzoom: 9,
            paint: {'fill-color': CARTO.allotments, 'fill-opacity': 0.75},
        },
        {
            id: 'landuse-cemetery',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            filter: ['==', ['get', 'class'], 'cemetery'],
            minzoom: 10,
            paint: {'fill-color': CARTO.cemetery, 'fill-opacity': 0.8},
        },
        // Текстура кладбища (крестики) поверх заливки — как в osm-carto.
        {
            id: 'landuse-cemetery-texture',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            filter: ['==', ['get', 'class'], 'cemetery'],
            minzoom: 13,
            paint: {'fill-pattern': 'pat-cemetery'},
        },
        {
            id: 'park',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'park',
            paint: {'fill-color': CARTO.grass, 'fill-opacity': 0.7},
        },

        // ── Вода ─────────────────────────────────────────────────────────────
        {
            id: 'water',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'water',
            filter: ['!=', ['get', 'brunnel'], 'tunnel'],
            paint: {'fill-color': CARTO.water},
        },
        {
            id: 'waterway',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'waterway',
            minzoom: 8,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.water,
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 12, 1.4, 16, 3],
            },
        },

        // ── Здания ───────────────────────────────────────────────────────────
        {
            id: 'building',
            type: 'fill',
            source: SOURCE_ID,
            'source-layer': 'building',
            minzoom: 13,
            paint: {
                'fill-color': CARTO.building,
                'fill-outline-color': CARTO.buildingOutline,
                'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 14.5, 0.9],
            },
        },

        // ── Дороги: обводки (casing) снизу, узкие заливки поверх ──────────────
        // osm-carto: сначала тёмная линия шире, поверх — светлая уже.
        {
            id: 'road-minor-casing',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['in', ['get', 'class'], ['literal', ['minor', 'service', 'track']]],
            minzoom: 11,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.minorCasing,
                'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 14, 3.4, 18, 12],
            },
        },
        {
            id: 'road-secondary-casing',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['in', ['get', 'class'], ['literal', ['secondary', 'tertiary']]],
            minzoom: 8,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.secondaryCasing,
                'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1, 12, 2.8, 14, 5.5, 18, 15],
            },
        },
        {
            id: 'road-primary-casing',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'primary'],
            minzoom: 7,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.primaryCasing,
                'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1, 10, 3, 14, 6.5, 18, 17],
            },
        },
        {
            id: 'road-trunk-casing',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'trunk'],
            minzoom: 6,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.trunkCasing,
                'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.2, 10, 3.4, 14, 7, 18, 18],
            },
        },
        {
            id: 'road-motorway-casing',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'motorway'],
            minzoom: 5,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.motorwayCasing,
                'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.4, 10, 4, 14, 8, 18, 20],
            },
        },

        // Заливки (поверх обводок)
        {
            id: 'road-path',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['in', ['get', 'class'], ['literal', ['path', 'pedestrian']]],
            minzoom: 14,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.path,
                'line-dasharray': [2, 2],
                'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.8, 18, 3],
            },
        },
        {
            id: 'road-minor',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['in', ['get', 'class'], ['literal', ['minor', 'service', 'track']]],
            minzoom: 11,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.minor,
                'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 14, 2.2, 18, 9],
            },
        },
        {
            id: 'road-secondary',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['in', ['get', 'class'], ['literal', ['secondary', 'tertiary']]],
            minzoom: 8,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.secondary,
                'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 12, 1.8, 14, 4, 18, 11],
            },
        },
        {
            id: 'road-primary',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'primary'],
            minzoom: 7,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.primary,
                'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.6, 10, 2, 14, 4.6, 18, 13],
            },
        },
        {
            id: 'road-trunk',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'trunk'],
            minzoom: 6,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.trunk,
                'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.7, 10, 2.2, 14, 5, 18, 14],
            },
        },
        {
            id: 'road-motorway',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'motorway'],
            minzoom: 5,
            layout: {'line-cap': 'round', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.motorway,
                'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.8, 10, 2.6, 14, 6, 18, 16],
            },
        },

        // ── Ж/д: серая линия + белые «шпалы» пунктиром поверх (вид osm-carto) ─
        {
            id: 'railway',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'rail'],
            minzoom: 10,
            layout: {'line-cap': 'butt', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.rail,
                'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.6, 14, 1.4, 18, 3],
            },
        },
        {
            id: 'railway-hatch',
            type: 'line',
            source: SOURCE_ID,
            'source-layer': 'transportation',
            filter: ['==', ['get', 'class'], 'rail'],
            minzoom: 13,
            layout: {'line-cap': 'butt', 'line-join': 'round'},
            paint: {
                'line-color': CARTO.minor,
                'line-dasharray': [0.4, 4],
                'line-width': ['interpolate', ['linear'], ['zoom'], 14, 2, 18, 6],
            },
        },

        // ── Подписи: порядок массива = ПРИОРИТЕТ КОЛЛИЗИЙ (низший → высший) ───
        // Шилды дорог → названия улиц → гидронимы → имена landuse/landcover →
        // POI → мелкие топонимы → крупные топонимы (последние выигрывают).

        // Номера дорог (ref) — в белом щитке с рамкой (как osm-carto): растяжимая
        // иконка 'road-shield' (генерится в рантайме, mapImages.ts) обжимается
        // вокруг текста через icon-text-fit:both.
        {
            id: 'label-road-ref',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'transportation_name',
            minzoom: 8,
            filter: ['all', ['has', 'ref'], ['!=', ['get', 'ref'], '']],
            layout: {
                'text-field': ['get', 'ref'],
                'text-font': ['IBM Plex Sans Medium'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 8, 9, 16, 12],
                'symbol-placement': 'line',
                'symbol-spacing': 350,
                'text-max-width': 6,
                'icon-image': 'road-shield',
                'icon-text-fit': 'both',
                'icon-text-fit-padding': [1, 3, 1, 3],
                'text-rotation-alignment': 'viewport',
                'icon-rotation-alignment': 'viewport',
            },
            paint: {
                'text-color': CARTO.label,
                // Halo не нужен — фон даёт щиток; лёгкий белый для сглаживания.
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 0.6,
            },
        },
        {
            id: 'label-road-minor',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'transportation_name',
            minzoom: 14,
            filter: ['in', ['get', 'class'], ['literal', ['minor', 'service', 'track']]],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 15, 10, 18, 13],
                'symbol-placement': 'line',
                'text-max-width': 9,
            },
            paint: {
                'text-color': CARTO.label,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },
        {
            id: 'label-road-major',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'transportation_name',
            minzoom: 11,
            filter: ['in', ['get', 'class'], ['literal', ['primary', 'secondary', 'tertiary', 'trunk']]],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 18, 14],
                'symbol-placement': 'line',
                'text-max-width': 9,
            },
            paint: {
                'text-color': CARTO.label,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },
        {
            id: 'label-waterway',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'waterway',
            minzoom: 12,
            filter: ['has', 'name'],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Italic'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 13, 11, 16, 14],
                'symbol-placement': 'line',
                'symbol-spacing': 350,
                'text-max-width': 8,
            },
            paint: {
                'text-color': CARTO.waterLabel,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },
        {
            id: 'label-water',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'water_name',
            minzoom: 9,
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Italic'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 16, 14],
                'text-max-width': 8,
            },
            paint: {
                'text-color': CARTO.waterLabel,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },

        // ── Имена landuse/landcover — наши расширения тайлов ──────────────────
        // Возврат утраченных ориентиров: леса/лесничества (landcover) и
        // СНТ/промзоны (landuse). symbol-placement point — по центроиду полигона.
        {
            id: 'label-landcover',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'landcover',
            minzoom: 10,
            filter: ['has', 'name'],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Italic'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 16, 13],
                'text-max-width': 8,
                'symbol-placement': 'point',
            },
            paint: {
                'text-color': CARTO.forestLabel,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },
        {
            id: 'label-landuse',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'landuse',
            minzoom: 11,
            filter: ['has', 'name'],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 16, 12],
                'text-max-width': 8,
                'symbol-placement': 'point',
            },
            paint: {
                'text-color': CARTO.landuseLabel,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },

        // ── Номера домов: слой housenumber, мелкий приглушённый текст ─────────
        // Как в osm-carto: появляются только вплотную (z17+), не мешают.
        {
            id: 'housenumber',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'housenumber',
            minzoom: 15,
            filter: ['has', 'housenumber'],
            layout: {
                'text-field': ['get', 'housenumber'],
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 17, 9, 19, 11],
                'text-max-width': 5,
            },
            paint: {
                'text-color': CARTO.landuseLabel,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1,
            },
        },

        // ── Парковки / заправки: иконка даже БЕЗ названия ────────────────────
        // osm-carto рисует «P» (и колонку заправки) независимо от наличия имени;
        // у парковок его обычно нет, поэтому фильтра `has name` тут НЕТ. Имя,
        // если есть, подписывается под иконкой. Стоит ДО именованных POI —
        // в коллизиях уступает магазинам, но при наличии места показывается.
        {
            id: 'poi-amenity',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'poi',
            minzoom: 15,
            filter: ['in', ['get', 'class'], ['literal', POI_DEDICATED_CLASSES]],
            layout: {
                'icon-image': POI_ICON,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 15, 0.75, 18, 1],
                'icon-allow-overlap': false,
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': 11,
                'text-max-width': 8,
                'text-anchor': 'top',
                'text-offset': [0, 0.95],
                'text-optional': true,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.poi,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1,
            },
        },

        // ── POI: иконка Maki по `class` + подпись под ней ────────────────────
        // 3 rank-тира OSM Bright (меньше rank = важнее): важные появляются
        // раньше (z13), прочие — позже (z15). Иконка обязательна, текст —
        // optional (в плотной застройке коллизии роняют подпись, но иконка
        // остаётся). Мусорные классы (шлагбаумы/переезды) отфильтрованы.
        {
            id: 'label-poi-3',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'poi',
            minzoom: 15,
            filter: [
                'all',
                ['has', 'name'],
                ['>=', ['coalesce', ['get', 'rank'], 0], 25],
                ['!', ['in', ['get', 'class'], ['literal', POI_NAMED_EXCLUDE]]],
            ],
            layout: {
                'icon-image': POI_ICON,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 15, 0.7, 18, 0.95],
                'icon-allow-overlap': false,
                'icon-optional': false,
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': 11,
                'text-max-width': 8,
                'text-anchor': 'top',
                'text-offset': [0, 0.95],
                'text-optional': true,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.poi,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1,
            },
        },
        {
            id: 'label-poi-2',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'poi',
            minzoom: 14,
            filter: [
                'all',
                ['has', 'name'],
                ['>=', ['coalesce', ['get', 'rank'], 0], 15],
                ['<=', ['coalesce', ['get', 'rank'], 9999], 24],
                ['!', ['in', ['get', 'class'], ['literal', POI_NAMED_EXCLUDE]]],
            ],
            layout: {
                'icon-image': POI_ICON,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 14, 0.72, 18, 1],
                'icon-allow-overlap': false,
                'icon-optional': false,
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': 11,
                'text-max-width': 8,
                'text-anchor': 'top',
                'text-offset': [0, 0.95],
                'text-optional': true,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.poi,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1,
            },
        },
        {
            id: 'label-poi-1',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'poi',
            minzoom: 13,
            filter: [
                'all',
                ['has', 'name'],
                ['<=', ['coalesce', ['get', 'rank'], 9999], 14],
                ['!', ['in', ['get', 'class'], ['literal', POI_NAMED_EXCLUDE]]],
            ],
            layout: {
                'icon-image': POI_ICON,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 13, 0.75, 18, 1],
                'icon-allow-overlap': false,
                'icon-optional': false,
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': 12,
                'text-max-width': 8,
                'text-anchor': 'top',
                'text-offset': [0, 0.95],
                'text-optional': true,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.poi,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1,
            },
        },

        // ── Мелкие топонимы (СНТ-посёлки, деревни, хутора, микрорайоны) ───────
        {
            id: 'label-place-minor',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'place',
            minzoom: 11,
            filter: [
                'in',
                ['get', 'class'],
                ['literal', ['suburb', 'quarter', 'neighbourhood', 'hamlet', 'isolated_dwelling', 'farm', 'locality']],
            ],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 16, 13],
                'text-max-width': 8,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.label,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.2,
            },
        },

        // ── Крупные топонимы (сёла / посёлки / города) — высший приоритет ─────
        {
            id: 'label-place-village',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'place',
            filter: ['==', ['get', 'class'], 'village'],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 15, 18],
                'text-max-width': 8,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.label,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.3,
            },
        },
        {
            id: 'label-place-town',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'place',
            filter: ['==', ['get', 'class'], 'town'],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Regular'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 8, 12, 14, 20],
                'text-max-width': 8,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.label,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.3,
            },
        },
        {
            id: 'label-place-city',
            type: 'symbol',
            source: SOURCE_ID,
            'source-layer': 'place',
            filter: ['==', ['get', 'class'], 'city'],
            layout: {
                'text-field': NAME,
                'text-font': ['IBM Plex Sans Medium'],
                'text-size': ['interpolate', ['linear'], ['zoom'], 6, 13, 12, 24],
                'text-max-width': 8,
                'symbol-sort-key': ['coalesce', ['get', 'rank'], 999],
            },
            paint: {
                'text-color': CARTO.label,
                'text-halo-color': CARTO.labelHalo,
                'text-halo-width': 1.4,
            },
        },
    ],
});